/*
* Description: Key Storage Server API and API endpoints
*/
import { IFetchResponse, KSSUserBase, KSSUser, WebAuthnKeyAuthenticate, WebAuthnKeyRegister, KSSAuthParams } from "@preveil-api";
import { AccountIdentifiers, ApiMethods, BaseAPI, REACT_APP_KEYSTORAGE_API_VERSION, ConfigKeys } from "src/common";
import { getConfigValue } from "src/store";

const default_headers: Record<string, string> = {
  "Content-Type": "application/json",
  "accept-version": REACT_APP_KEYSTORAGE_API_VERSION || "v0"
};

function buildAuthorizationHeader(params: KSSAuthParams): Record<string, string> {
  const auth_parts: string[] = [];

  // Simple Mappings for Basic Codes
  const simple_mappings: Record<string, string | undefined> = {
    "sms": params.sms_code,
    "newsms": params.new_sms,
    "newtotp": params.new_totp,
    "totp": params.totp_code,
    "email": params.email_code
  };

  for (const [key, value] of Object.entries(simple_mappings)) {
    if (value) auth_parts.push(`${key} ${value}`);
  }

  // Handle WebAuthn Data Separately
  const web_auth_mappings: Record<string, WebAuthnKeyAuthenticate | undefined> = {
    "webauthn": params.web_auth_key,
    "newwebauthn": params.new_web_auth_key,
  };

  for (const [key, webAuthnData] of Object.entries(web_auth_mappings)) {
    if (webAuthnData) {
      const { client_data, auth_data, signature } = webAuthnData;
      auth_parts.push(`${key} ${client_data} ${auth_data} ${signature}`);
    }
  }

  // Combine parts with a semicolon
  return auth_parts.length > 0 ? { "Authorization": auth_parts.join(";") } : {};
}
// --------------------------------------------------------------------------------------------
// Saga calls
// NOTE: base_url needs to be filled dynamically from store state App > Config > keystorage_server
// --------------------------------------------------------------------------------------------
/*
 * Description: Get KSS User with or without SMS code
 * Request: GET; 
 *    @params: user_id: string
 *    @params: auth_token: string - already stringified (b64Encoded)
 *    @params: sms_code?: string - optional - will return error but will send out SMS code to stored phone
 * Response: { ... }
 */
const getUser = (kss_user: KSSUserBase): Promise<IFetchResponse> => {
  const headers = Object.assign({}, default_headers, buildAuthorizationHeader(kss_user), { "x-auth-token": kss_user.auth_token });
  const url = `${getConfigValue(ConfigKeys.keystorage_server)}/users/${kss_user.user_id}`;
  return BaseAPI.get(url, undefined, headers);
}

/*
 * Description: Create a new KSS User with or without SMS code 
 * Request: POST; 
 *    @params: kss_user: KSSUser
 *    @params: auth_token: string - already stringified (b64Encoded)
 *    @params: sms_code?: string - optional - will return error but will send out SMS code to stored phone
 *    @header:  "Authorization": `sms ${kss_user.sms_code}`
 * Response: { ... }
 */
const createUser = (kss_user: Partial<KSSUser>, sms_code?: string): Promise<IFetchResponse> => {
  const headers = !!sms_code ? Object.assign({}, default_headers, { "Authorization": `sms ${sms_code}` }) : default_headers;
  return BaseAPI.post(`${getConfigValue(ConfigKeys.keystorage_server)}/users`, kss_user, headers);
}

const rekeyUser = async (account_ids: AccountIdentifiers, auth_token?: string, wrapped_data?: string, server_shard?: string, verify_key?: string, new_auth_token?: string): Promise<IFetchResponse> => {
  const url = `/users/${account_ids.user_id}`;
  const current_key_version = account_ids.user_key.key_version;
  const key_version = !!wrapped_data ? !!new_auth_token ? current_key_version : current_key_version + 1 : undefined;
  const body = { auth_token, wrapped_data, key_version, server_shard, verify_key, new_auth_token };
  const sign_only_with_user_signature = !!new_auth_token;
  const headers = await BaseAPI.prepareSignedRequestHeader(account_ids, url, ApiMethods.patch, body, sign_only_with_user_signature);
  return BaseAPI.patch(`${getConfigValue(ConfigKeys.keystorage_server)}${url}`, body, headers);
}

const isRecoverySetUp = async (account_ids: AccountIdentifiers, auth_token: string): Promise<IFetchResponse> => {
  const url = `/users/${account_ids.user_id}/shard_metadata`;
  const headers = Object.assign(await BaseAPI.prepareSignedRequestHeader(account_ids, url, ApiMethods.get, null), { "x-auth-token": auth_token });
  return BaseAPI.get(`${getConfigValue(ConfigKeys.keystorage_server)}${url}`, null, headers);
}

const getServerShard = async (user_id: string, auth_params: KSSAuthParams): Promise<IFetchResponse> => {
  const url = `/users/${user_id}/shard`;
  const headers = Object.assign({}, default_headers, buildAuthorizationHeader(auth_params));
  return BaseAPI.get(`${getConfigValue(ConfigKeys.keystorage_server)}${url}`, undefined, headers);
}

const changeAuthenticationMethod = async (account_ids: AccountIdentifiers, auth_params: KSSAuthParams, auth_token?: string, phone_number?: string, totp_secret?: string, new_web_auth_credential?: WebAuthnKeyRegister): Promise<IFetchResponse> => {
  const url = `/users/${account_ids.user_id}/secondary_auth_method`;
  const body = { auth_token, phone_number, totp_secret, totp: auth_params.totp_code, sms: auth_params.sms_code, webauthn_credential: new_web_auth_credential };
  const headers = Object.assign({}, default_headers, await BaseAPI.prepareSignedRequestHeader(account_ids, url, ApiMethods.patch, body), buildAuthorizationHeader(auth_params));
  return BaseAPI.patch(`${getConfigValue(ConfigKeys.keystorage_server)}${url}`, body, headers);
}

export const KeyStorageAPI = {
  getUser,
  createUser,
  rekeyUser,
  getServerShard,
  isRecoverySetUp,
  changeAuthenticationMethod
}

// --------------------------------------------------------------------------------------------
// RTK Queries Calls:  WORKING
// --------------------------------------------------------------------------------------------
// API and Request API Types
export type GetKSSUsersApiResponse = /** status 200 successful operation */ {
  protocol_version: number;
  wrapped_data: string;
}
export type GetKSSUsersApiError = /** status 401 unauthorized error response */ {
  error: string;
}
